import React, { useContext } from "react";
import Layout from "@/components/custom/Layout";
import IndexPage from "@/components/custom/IndexPage";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Login from "@/components/custom/Auth/Login";
import ForgotPassword from "@/components/custom/Auth/ForgotPassword";
import ProtectedRoute from "@/components/custom/Auth/ProtectedRoute";
import Home from "@/components/custom/Home/Home";
import ArchivedCasesTable from "@/components/custom/Cases/ArchivedCasesTable";
import AttorneyContactTable from "@/components/custom/Contacts/AttorneyContactTable";
import UsersTable from "@/components/custom/Users/UsersTable";
import TimelinesTable from "@/components/custom/Timelines/TimelinesTable";
import ResearchFolders from "@/components/custom/Research/Folders/ResearchFolders";
import CaseSourceTable from "@/components/custom/Sources/CaseSourceTable";
import DeadlineTable from "@/components/custom/Deadlines/DeadlineTable";
import SearchPage from "@/components/custom/Search";
import ManageCase from "@/components/custom/Cases/Manage";
import SourceDetails from "@/components/custom/Sources/SourceDetails";
import PrintTimeline from "@/components/custom/Timelines/PrintTimeline";
import Setup2FA from "@/components/custom/Setup2FA";
import {
  useAdminPermission,
  useITAdminPermission,
  usePermission,
} from "@/hooks/usePermission";
import PageNotFound from "@/components/custom/PageNotFound";
import CaseTimerTable from "@/components/custom/CaseTimer/CaseTimerTable";
import TimeTrackerWidget from "@/components/custom/CaseTimer/TimeTrackerWidget";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { CustomSiteSettingsPage } from "@/components/custom/CustomSiteSettings";
import ScientificResearchTable from "@/components/custom/ScientificResearches/ScientificResearchTable";
import { useTenantRestriction } from "@/hooks/useTenantRestriction";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import UserIdle from "@/components/custom/UserIdle";
import { EULAModal } from "@/components/custom/EULAModal";
import UserEmailSettings from "@/components/custom/Users/UserEmailSettings";
import EventSourceViewer from "@/components/custom/Events/EventSourceViewer";
import AIGeneratedSourceEvents from "@/components/custom/Sources/AIGeneratedSourceEvents";
import { useHasAIAccess } from "@/hooks/useHaseAIAccess";

function App() {
  const hasAdminPermission = useAdminPermission();
  const hasResearchPermission = usePermission("research.add_research");
  const { values: contextValues } = useContext(AuthContext) as AuthContextType;
  const { user, loading } = contextValues;
  const tenantResearchLimitations = useTenantRestriction("Research");
  const { t } = useTranslation();
  const isItAdmin = useITAdminPermission();
  const hasAIAccess = useHasAIAccess();

  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route index element={<IndexPage />} />
          <Route path="login" element={<Login />} />
          <Route path="accounts/login" element={<Login />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route
            path="Home"
            element={
              <ProtectedRoute>
                {isItAdmin ? <UsersTable /> : <Home />}
              </ProtectedRoute>
            }
          />
          <Route
            path="archived-cases"
            element={
              <ProtectedRoute>
                <ArchivedCasesTable />
              </ProtectedRoute>
            }
          />
          {!!hasAdminPermission && (
            <>
              <Route
                path="users"
                element={
                  <ProtectedRoute>
                    <UsersTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="site-settings"
                element={
                  <ProtectedRoute>
                    <CustomSiteSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="attorney-contacts"
                element={
                  <ProtectedRoute>
                    <AttorneyContactTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="deadlines"
                element={
                  <ProtectedRoute>
                    <DeadlineTable />
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {!!hasResearchPermission && (
            <Route
              path="research-folders"
              element={
                <ProtectedRoute>
                  <ResearchFolders />
                </ProtectedRoute>
              }
            />
          )}

          <Route
            path="scientific-research"
            element={
              <ProtectedRoute>
                {!tenantResearchLimitations.hasAccess ? (
                  <div className="w-full flex flex-col justify-center items-center text-center p-4">
                    <Lock className="h-10 w-10 text-primary mb-6" />
                    <div className="items-center">
                      <h2 className="text-xl font-bold items-center">
                        {t("common.statictext.access_denied")}
                      </h2>
                      <p className="pt-4">
                        {tenantResearchLimitations.message}
                      </p>
                      <Button
                        className="mt-6"
                        variant="default"
                        onClick={tenantResearchLimitations.requestUpgrade}
                        disabled={loading}
                      >
                        {loading
                          ? t("common.statictext.sending_request")
                          : t("common.statictext.request_upgrade")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <ScientificResearchTable />
                )}
              </ProtectedRoute>
            }
          />

          <Route
            path="timer"
            element={
              <ProtectedRoute>
                <CaseTimerTable />
              </ProtectedRoute>
            }
          />
          <Route
            path="sources"
            element={
              <ProtectedRoute>
                <CaseSourceTable />
              </ProtectedRoute>
            }
          />
          <Route
            path="sources/:id"
            element={
              <ProtectedRoute>
                <SourceDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="timelines"
            element={
              <ProtectedRoute>
                <TimelinesTable />
              </ProtectedRoute>
            }
          />
          <Route
            path="search"
            element={
              <ProtectedRoute>
                <SearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="cases/:id"
            element={
              <ProtectedRoute>
                <ManageCase />
              </ProtectedRoute>
            }
          />
          <Route
            path="timelines/case/:id"
            element={
              <ProtectedRoute>
                <PrintTimeline />
              </ProtectedRoute>
            }
          />
          <Route
            path="setup-2fa"
            element={
              <ProtectedRoute>
                <Setup2FA />
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-settings"
            element={
              <ProtectedRoute>
                <UserEmailSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="events/:eventId/source-viewer"
            element={
              <ProtectedRoute>
                <EventSourceViewer />
              </ProtectedRoute>
            }
          />
          {hasAIAccess && (
            <Route
              path="sources/:id/ai"
              element={
                <ProtectedRoute>
                  <AIGeneratedSourceEvents />
                </ProtectedRoute>
              }
            />
          )}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Layout>
      {!!user?.user.id && <TimeTrackerWidget />}
      {!!user?.user.id && <UserIdle />}
      {!!user?.user.id && <EULAModal />}
    </BrowserRouter>
  );
}

export default App;
